import Microgame from '../engine/Microgame.js';

export default class ScaleGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Balance !`, en: `Scale!`, es: `¡Báscula!` },
            controls: { fr: `Glisse des poids d'un côté pour équilibrer`, en: `Drag weights to one side to balance`, es: `Arrastra pesos a un lado para equilibrar` },
            objective: { fr: `Équilibre parfaitement la balance des deux côtés`, en: `Balance the scale perfectly on both sides`, es: `Equilibra perfectamente la báscula de ambos lados` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M12 22v-6M12 2v6"/><path d="M4 16h16"/><path d="m6 16-2 6h16l-2-6"/></svg>`,
            actions: [
                { type: 'tap', x: 50, y: 70, duration: 120 },
                { type: 'wait', duration: 120 },
                { type: 'tap', x: 50, y: 70, duration: 120 },
                { type: 'wait', duration: 120 },
                { type: 'tap', x: 50, y: 70, duration: 120 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.beam = null;
        this.leftPan = null;
        this.rightPan = null;
        this.leftWeight = 0;
        this.rightWeight = 0;
        this.angle = 0;
        this.targetWeight = 0;
        this.currentWeight = 0;
        this.winTimer = null;

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;

        // Tighter curve: 2200ms at x1, ~760ms at x4 (+100ms tweak)
        const adjustedSpeed = 1 + (speedMult - 1) * (1.89 / 3);
        this.duration = 2200 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "BALANCE!";

        // Base
        const base = document.createElement('div');
        base.style.position = 'absolute';
        base.style.width = '10px';
        base.style.height = '150px';
        base.style.background = this.secondaryColor;
        base.style.bottom = '20%';
        base.style.left = '50%';
        base.style.transform = 'translateX(-50%)';
        this.container.appendChild(base);

        // Beam
        this.beam = document.createElement('div');
        this.beam.style.position = 'absolute';
        this.beam.style.width = '200px';
        this.beam.style.height = '6px';
        this.beam.style.background = this.secondaryColor;
        this.beam.style.top = '0';
        this.beam.style.left = '50%';
        this.beam.style.transform = 'translate(-50%, -50%)'; // Pivot center
        this.beam.style.transformOrigin = 'center';
        base.appendChild(this.beam);

        // Pans
        this.leftPan = this.createPan(this.beam, 'left');
        this.rightPan = this.createPan(this.beam, 'right');

        // Initial Weights
        // Target is random 2-5
        this.targetWeight = this.isPlayback ? 3 : Math.floor(Math.random() * 3) + 2;
        this.currentWeight = 0;

        // Put target weight on LEFT
        this.addWeight(this.leftPan, this.targetWeight, true);

        // Right side is empty initially

        this.updateBalance();

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    createPan(parent, side) {
        const string = document.createElement('div');
        string.style.position = 'absolute';
        string.style.width = '2px';
        string.style.height = '60px';
        string.style.background = this.secondaryColor;
        string.style.top = '0';
        if (side === 'left') string.style.left = '0';
        else string.style.right = '0';
        parent.appendChild(string);

        const pan = document.createElement('div');
        pan.style.position = 'absolute';
        pan.style.width = '50px';
        pan.style.height = '10px';
        pan.style.background = this.secondaryColor;
        pan.style.bottom = '0';
        pan.style.left = '50%';
        pan.style.transform = 'translate(-50%, 0)';
        pan.style.borderRadius = '0 0 25px 25px';
        string.appendChild(pan);

        return pan;
    }

    addWeight(pan, count, isTarget) {
        for (let i = 0; i < count; i++) {
            const w = document.createElement('div');
            w.style.position = 'absolute';
            w.style.width = '20px';
            w.style.height = '20px';
            w.style.background = isTarget ? '#555' : this.primaryColor;
            w.style.bottom = `${10 + i * 22}px`;
            w.style.left = '50%';
            w.style.transform = 'translateX(-50%)';
            w.style.borderRadius = '4px';
            if (!isTarget) w.style.boxShadow = `0 0 10px ${this.primaryColor}`;
            pan.appendChild(w);
        }
    }

    updateBalance() {
        // Calculate angle based on weight diff
        const diff = this.targetWeight - this.currentWeight;

        let targetAngle = -diff * 10;
        targetAngle = Math.max(-45, Math.min(45, targetAngle));

        this.angle = targetAngle;
        this.beam.style.transition = 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        this.beam.style.transform = `translate(-50%, -50%) rotate(${this.angle}deg)`;

        // Clear any pending win
        if (this.winTimer) {
            clearTimeout(this.winTimer);
            this.winTimer = null;
        }

        if (this.currentWeight === this.targetWeight) {
            this.hideTimer = true; // Stop timer visually while waiting
            // Wait to confirm win (allow user to mess up by tapping again)
            this.winTimer = setTimeout(() => {
                if (this.active && this.currentWeight === this.targetWeight) {
                    this.win();
                    this.beam.style.background = this.primaryColor;
                }
            }, 200); // reduced from 500ms to 200ms for high speed feasibility
        } else if (this.currentWeight > this.targetWeight) {
            this.lose();
        }
    }

    onDown() {
        if (!this.active || this.won) return;

        this.currentWeight++;

        // Add visual weight to right pan
        const w = document.createElement('div');
        w.style.position = 'absolute';
        w.style.width = '20px';
        w.style.height = '20px';
        w.style.background = this.primaryColor;
        w.style.bottom = `${10 + (this.currentWeight - 1) * 22}px`;
        w.style.left = '50%';
        w.style.transform = 'translateX(-50%) scale(0)';
        w.style.borderRadius = '4px';
        w.style.boxShadow = `0 0 10px ${this.primaryColor}`;
        w.style.transition = 'transform 0.2s';
        this.rightPan.appendChild(w);

        // Animate in
        requestAnimationFrame(() => {
            w.style.transform = 'translateX(-50%) scale(1)';
        });

        this.updateBalance();
    }

    cleanup() {
        super.cleanup();
        if (this.winTimer) clearTimeout(this.winTimer);
        this.hideTimer = false;
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
